import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus } from 'lucide-react';

import LocationFormDetailsTab from './LocationFormDetailsTab';
import LocationFormHoursTab from './LocationFormHoursTab';
import LocationFormAmenitiesTab from './LocationFormAmenitiesTab';

// Wraps the 3-tab Add/Edit dialog. Receives all state + actions from the
// hook so the shell stays trivial.
export default function LocationFormDialog({
  isOpen, onOpenChange, editingLocation, activeTab, onTabChange,
  formData, onUpdateField,
  onUpdateOperatingHours, onAddBreak, onUpdateBreakField, onRemoveBreak,
  onToggleSport, onToggleAmenity,
  onSave, onOpenCreate,
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="add-location-button" className="btn-volt" onClick={onOpenCreate}>
          <Plus className="w-5 h-5 mr-2" />
          Add Location
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-xl">
            {editingLocation ? 'Edit Location' : 'Add New Location'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {editingLocation
              ? 'Edit location details, operating hours, sports and amenities.'
              : 'Create a new branch location with address, operating hours, sports and amenities.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={onTabChange} className="mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="hours">Operating Hours</TabsTrigger>
            <TabsTrigger value="amenities">Sports & Amenities</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 mt-4">
            <LocationFormDetailsTab formData={formData} onUpdateField={onUpdateField} />
          </TabsContent>

          <TabsContent value="hours">
            <LocationFormHoursTab
              formData={formData}
              onUpdateOperatingHours={onUpdateOperatingHours}
              onAddBreak={onAddBreak}
              onUpdateBreakField={onUpdateBreakField}
              onRemoveBreak={onRemoveBreak}
            />
          </TabsContent>

          <TabsContent value="amenities">
            <LocationFormAmenitiesTab
              formData={formData}
              onToggleSport={onToggleSport}
              onToggleAmenity={onToggleAmenity}
            />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} className="btn-volt" data-testid="save-location-btn">
            {editingLocation ? 'Update Location' : 'Create Location'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
